/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { AssetOption, OptionUtils, RecordingOption, RecordingOptionType } from '@wit/vtv-x-foundation-lib';
import { useCallback, useEffect, useMemo, useState } from 'react';
import translate from 'services/i18n/translate';

interface ConfirmationModalProps {
  message: string;
  option: AssetOption;
  callBack: (option: AssetOption) => void;
}

export function useRecordingOptions(
  setConfirmationModal: React.Dispatch<React.SetStateAction<ConfirmationModalProps | undefined>>,
  setShowConfirmationModal: React.Dispatch<React.SetStateAction<boolean>>,
  runOptionIsProcessed: boolean,
  dispatchOption: (option: AssetOption) => void,
  deletedRecordingCallback?: () => void
) {
  const [deleteRecordingOption, setDeleteRecordingOption] = useState<RecordingOption | undefined>();

  useEffect(() => {
    if (deleteRecordingOption && runOptionIsProcessed) {
      setDeleteRecordingOption(undefined);
      if (deletedRecordingCallback) {
        deletedRecordingCallback();
      }
    }
  }, [deletedRecordingCallback, deleteRecordingOption, runOptionIsProcessed]);

  const createConfirmationModal = useCallback(
    (message: string, option: AssetOption, callBack: (option: AssetOption) => void) => {
      const modal: ConfirmationModalProps = {
        message,
        option,
        callBack,
      };
      setConfirmationModal(modal);
      setShowConfirmationModal(true);
    },
    [setConfirmationModal, setShowConfirmationModal]
  );

  const dispatchRecordingOption = useCallback(
    (option: AssetOption) => {
      dispatchOption(option);
      setShowConfirmationModal(false);
    },
    [dispatchOption, setShowConfirmationModal]
  );

  const runDeleteRecordingOption = useCallback(
    (option: AssetOption) => {
      if (OptionUtils.isRecording(option)) {
        dispatchRecordingOption(option);
        setDeleteRecordingOption(option);
      }
    },
    [dispatchRecordingOption]
  );

  const runRecordingOption = useCallback(
    (option: RecordingOption) => {
      switch (option.recordingOptionType) {
        case RecordingOptionType.DELETE_SINGLE:
        case RecordingOptionType.DELETE_EPISODE: {
          createConfirmationModal(
            translate['modal.recordings.confirmDeleteEpisodeRecording'],
            option,
            runDeleteRecordingOption
          );
          break;
        }
        case RecordingOptionType.CANCEL_EPISODE:
        case RecordingOptionType.CANCEL_SINGLE: {
          createConfirmationModal(
            translate['modal.recordings.confirmCancelEpisodeRecording'],
            option,
            dispatchRecordingOption
          );
          break;
        }
        case RecordingOptionType.CANCEL_SERIES: {
          createConfirmationModal(
            translate['modal.recordings.confirmCancelSeriesRecording'],
            option,
            dispatchRecordingOption
          );
          break;
        }
        case RecordingOptionType.DELETE_SERIES: {
          createConfirmationModal(
            translate['modal.recordings.confirmDeleteSeriesRecording'],
            option,
            runDeleteRecordingOption
          );
          break;
        }
        default:
          dispatchRecordingOption(option);
      }
    },
    [createConfirmationModal, runDeleteRecordingOption, dispatchRecordingOption]
  );

  return useMemo(() => {
    return { runRecordingOption };
  }, [runRecordingOption]);
}
